import { Link } from 'react-router';
import clsx from 'clsx';

import { MEDIA } from '../../constants/media';

// Import constants
import {
  SUPPORT_MAILTO,
  FEEDBACK_MAILTO,
  BUG_REPORT_MAILTO,
  SUPPORT_US_URL,
  FEEDBACK_ARIA_LABEL,
  FEEDBACK_TITLE,
  BUG_REPORT_ARIA_LABEL,
  BUG_REPORT_TITLE,
  SUPPORT_US_ARIA_LABEL,
  SUPPORT_US_TITLE,
  APP_STORE_URL,
  PLAY_STORE_URL,
  LINKEDIN_URL,
  YOUTUBE_URL,
} from '../../constants/links';

// Import utils
import { scrollToId } from '../../utils/scroll';

// Import hooks
import useHashLinkClick from '../../hooks/ui/useHashLinkClick';

// Import components
import IconButton from '../button/IconButton';

export default function Footer() {
  const onHashLinkClick = useHashLinkClick();

  const handleLogoClick = () => {
    scrollToId('hero');
  };

  return (
    <footer className="bg-green-700 text-white py-4">
      <div className="max-w-5xl mx-auto p-4 flex flex-col md:flex-row md:justify-between gap-6">
        {/* Title */}
        <div className="flex flex-col text-start md:w-1/3">
          <div className="flex items-center gap-4 mb-2">
            <img
              src={MEDIA.logo}
              alt="WhenWeMeet"
              className="h-12 w-12 rounded-2xl object-cover hover:shadow-2xl transition-shadow cursor-pointer"
              onClick={handleLogoClick}
              loading="lazy"
              decoding="async"
            />
            <div>
              <h3 className="text-xl font-semibold">WhenWeMeet?</h3>
              <p className="text-base">Plan together, faster.</p>
            </div>
          </div>
          <p className="text-xs">
            WhenWeMeet is an online scheduling platform designed to simplify
            group meeting scheduling. It helps users find the time that works
            for everyone by collecting availability and identifying the ideal
            meeting time without calendar conflicts.
          </p>
          <div className="flex gap-4 mt-2">
            <IconButton
              name="linkedin"
              color="white"
              size={32}
              onClick={() => window.open(LINKEDIN_URL)}
            />
            <IconButton
              name="mail"
              color="white"
              size={32}
              style={{ paddingTop: '2px' }}
              onClick={() => (window.location.href = SUPPORT_MAILTO)}
            />
            <IconButton
              name="coffee"
              color="white"
              size={34}
              style={{ marginTop: '-2px' }}
              onClick={() => window.open(SUPPORT_US_URL)}
            />
            <IconButton
              name="youtube"
              color="white"
              size={35}
              style={{ paddingTop: '2px' }}
              onClick={() => window.open(YOUTUBE_URL)}
            />
          </div>
        </div>

        {/* Get the app */}
        <div className="flex flex-col items-start">
          <Link to="/app" className="hover:underline">
            <h4 className="flex text-base font-bold mb-1">Get the App</h4>
          </Link>
          <ul className="flex flex-col gap-1 text-left text-base">
            <li>
              <a
                href={APP_STORE_URL}
                target="_blank"
                className={clsx(['hover:underline'])}
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
                title="Download on the App Store">
                App Store
              </a>
            </li>
            <li>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                className={clsx(['hover:underline'])}
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
                title="Get it on Google Play">
                Google Play
              </a>
            </li>
          </ul>
        </div>

        {/* Legal */}
        <div className="flex flex-col items-start">
          <h4 className="flex text-base font-bold mb-1">Legal</h4>
          <ul className="flex flex-col gap-1 text-left text-base">
            <li>
              <Link to="/terms" className="hover:underline">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:underline">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>

        {/* Support */}
        <div className="flex flex-col items-start">
          <h4 className="flex text-base font-bold mb-1">Support</h4>
          <ul className="flex flex-col gap-1 text-left text-base">
            <li>
              <a
                href={FEEDBACK_MAILTO}
                className="hover:underline"
                aria-label={FEEDBACK_ARIA_LABEL}
                title={FEEDBACK_TITLE}>
                Feedback
              </a>
            </li>
            <li>
              <a
                href={BUG_REPORT_MAILTO}
                className="hover:underline"
                aria-label={BUG_REPORT_ARIA_LABEL}
                title={BUG_REPORT_TITLE}>
                Bug Report
              </a>
            </li>
            <li>
              <Link to="/support" className="hover:underline">
                Support
              </Link>
            </li>
          </ul>
        </div>

        {/* Links */}
        <div className="flex flex-col items-start">
          <h4 className="flex text-base font-bold mb-1">Links</h4>
          <ul className="flex flex-col gap-1 text-left text-base">
            <li>
              <Link
                to="/#features"
                className="hover:underline"
                onClick={(e) => onHashLinkClick(e, 'features')}>
                Features
              </Link>
            </li>
            <li>
              <Link
                to="/#how-to-use"
                className="hover:underline"
                onClick={(e) => onHashLinkClick(e, 'how-to-use')}>
                How it works
              </Link>
            </li>
            <li>
              <Link
                to="/#pricing"
                className="hover:underline"
                onClick={(e) => onHashLinkClick(e, 'pricing')}>
                Pricing
              </Link>
            </li>
            <li>
              <Link to="/link-devices" className="hover:underline">
                Link Devices
              </Link>
            </li>
            <li>
              <a
                href={SUPPORT_US_URL}
                target="_blank"
                className="hover:underline"
                rel="noopener noreferrer"
                aria-label={SUPPORT_US_ARIA_LABEL}
                title={SUPPORT_US_TITLE}>
                Support Us
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6">
        <div className="max-w-5xl mx-auto px-4">
          <div
            className="h-px w-full bg-linear-to-r from-transparent via-white/25 to-transparent"
            aria-hidden="true"
          />

          <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:items-center sm:justify-between text-sm text-green-100/90">
            <span>© {new Date().getFullYear()} WhenWeMeet LLC</span>
            <span className="sm:text-right">Group Meeting Scheduling</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
